import { useState, useEffect } from "react";
import axios from "axios";
import {
    Activity, DollarSign,
    Search, RefreshCw, ChevronLeft, ChevronRight,
    Trophy, Users, BarChart3, X, Filter,
    ArrowUpRight, ArrowDownRight, FileSpreadsheet, FileText,
} from "lucide-react";

const API_BASE = "https://api.playbro.com";

interface Transaction {
    id: string;
    transaction_id: string;
    user_name: string;
    user_id: string;
    game_title?: string;
    transaction_type: string;
    amount: number;
    currency_code: string;
    transaction_timestamp: string;
}

interface PaginationState {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    limit: number;
}

/* Shape returned by GET /betHistory/transactions/admin-analytics */
interface Analytics {
    summary: {
        total_bets: number;
        total_wins: number;
        total_losses: number;
        total_wagered: number;
        total_payout: number;
        net_profit: number;
        total_loss_amount: number;
        unique_players: number;
    };
    gameBreakdown: { game_name: string; bet_count: number; wagered: number; payout: number; net_profit: number; players: number }[];
    dailyTrend: { day: string; bets: number; wagered: number; net_profit: number; players: number }[];
    gamesList: string[];
}

const fmtNum = (n: number) =>
    Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d: string) =>
    new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const TYPE_COLORS: Record<string, string> = {
    BET: "bg-[#2B6EF5]/20 text-[#4A85F7]",
    WIN: "bg-emerald-500/20 text-emerald-400",
    LOSS: "bg-[#E01B4F]/20 text-[#E01B4F]",
    ROLLBACK: "bg-[#FFC23F]/20 text-[#FFC23F]",
    bet: "bg-[#2B6EF5]/20 text-[#4A85F7]",
    win: "bg-emerald-500/20 text-emerald-400",
    loss: "bg-[#E01B4F]/20 text-[#E01B4F]",
    rollback: "bg-[#FFC23F]/20 text-[#FFC23F]",
};

const History: React.FC = () => {
    const staffId = localStorage.getItem("currentUserId");
    const token = localStorage.getItem("token");

    const headers: Record<string, string> = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(staffId ? { "x-staff-id": staffId } : {}),
    };

    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [analytics, setAnalytics] = useState<Analytics | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [userId, setUserId] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationState>({ totalCount: 0, totalPages: 0, currentPage: 1, limit: 15 });
    const [loading, setLoading] = useState(false);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<"overview" | "transactions">("overview");
    const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);

    // Fetch analytics
    const fetchAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
            const { data } = await axios.get(`${API_BASE}/betHistory/transactions/admin-analytics`, { headers });
            setAnalytics(data);
        } catch (e) { console.error(e); }
        finally { setAnalyticsLoading(false); }
    };

    // Normalize transaction shape from different API responses
    const normalizeTx = (tx: any): Transaction => ({
        id: tx.id || tx.transaction_id || "",
        transaction_id: tx.transaction_id || String(tx.id || ""),
        user_name: tx.user_name || tx.name || "",
        user_id: String(tx.user_id || tx.uid || ""),
        game_title: tx.game_title || tx.match_title || tx.game || null,
        transaction_type: tx.transaction_type || (tx.profit > 0 ? "WIN" : tx.profit < 0 ? "LOSS" : tx.reason || tx.bet_type || "BET"),
        amount: Math.abs(parseFloat(tx.amount) || 0),
        currency_code: tx.currency_code || tx.currency || tx.coin || "",
        transaction_timestamp: tx.transaction_timestamp || tx.created_at || tx.created || tx.timestamp || "",
    });

    // Build the transactions URL for the current filters
    const buildUrl = (page: number, limit: number) => {
        let url = `${API_BASE}/betHistory/transactions?page=${page}&limit=${limit}`;
        if (userId) url = `${API_BASE}/betHistory/transactions/user/${encodeURIComponent(userId)}?page=${page}&limit=${limit}&game_type=all`;
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        return url;
    };

    // Fetch transactions
    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const url = buildUrl(pagination.currentPage, pagination.limit);

            const { data } = await axios.get(url, { headers });
            const raw = data.transactions || [];
            setTransactions(raw.map(normalizeTx));
            setPagination(data.pagination || pagination);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchAnalytics(); }, []);
    useEffect(() => { fetchTransactions(); }, [pagination.currentPage, userId]);

    // Debounced search
    useEffect(() => {
        const t = setTimeout(() => {
            setPagination(p => ({ ...p, currentPage: 1 }));
            fetchTransactions();
        }, 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const handleClear = () => { setSearchTerm(""); setUserId(null); setPagination(p => ({ ...p, currentPage: 1 })); };

    /* ── Export ──
     * Exports every row matching the CURRENT filters (not just the visible page),
     * so a user-id / search filtered audit exports in full.
     */
    const EXPORT_LIMIT = 100000;

    const fetchAllForExport = async (): Promise<Transaction[]> => {
        const { data } = await axios.get(buildUrl(1, EXPORT_LIMIT), { headers });
        return (data.transactions || []).map(normalizeTx);
    };

    const signedAmount = (tx: Transaction) => {
        const t = (tx.transaction_type || "").toLowerCase();
        const abs = Math.abs(tx.amount);
        return t === "win" ? abs : t === "bet" || t === "loss" ? -abs : abs;
    };

    const exportFileName = (ext: string) => {
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        const scope = userId ? `user-${userId}` : searchTerm ? `search-${searchTerm}` : "all";
        return `bet-history_${scope}_${stamp}.${ext}`;
    };

    const exportExcel = async () => {
        setExporting("excel");
        try {
            // Loaded on demand — keeps ~200 kB of export libs out of the main bundle.
            const XLSX = await import("xlsx");
            const rows = await fetchAllForExport();
            if (!rows.length) { alert("No transactions to export."); return; }

            const sheetRows = rows.map((tx, i) => ({
                "#": i + 1,
                "Transaction ID": tx.transaction_id,
                "User": tx.user_name,
                "User ID": tx.user_id,
                "Game": tx.game_title || "—",
                "Coin": (tx.currency_code || "").toUpperCase(),
                "Type": (tx.transaction_type || "").toUpperCase(),
                "Amount": Number(signedAmount(tx).toFixed(8)),
                "Date": fmtDate(tx.transaction_timestamp),
            }));

            const totals = rows.reduce(
                (a, tx) => {
                    const t = (tx.transaction_type || "").toLowerCase();
                    if (t === "bet") a.wagered += Math.abs(tx.amount);
                    if (t === "win") a.payout += Math.abs(tx.amount);
                    return a;
                },
                { wagered: 0, payout: 0 }
            );

            const ws = XLSX.utils.json_to_sheet(sheetRows);
            ws["!cols"] = [
                { wch: 6 }, { wch: 24 }, { wch: 18 }, { wch: 14 }, { wch: 22 },
                { wch: 8 }, { wch: 10 }, { wch: 14 }, { wch: 22 },
            ];

            // Summary block appended below the data
            XLSX.utils.sheet_add_aoa(
                ws,
                [
                    [],
                    ["Total Rows", rows.length],
                    ["Total Wagered", Number(totals.wagered.toFixed(8))],
                    ["Total Payout", Number(totals.payout.toFixed(8))],
                    ["Net (Payout - Wagered)", Number((totals.payout - totals.wagered).toFixed(8))],
                    ["Filter", userId ? `User ID: ${userId}` : searchTerm ? `Search: ${searchTerm}` : "None"],
                    ["Generated", fmtDate(new Date().toISOString())],
                ],
                { origin: -1 }
            );

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Bet History");
            XLSX.writeFile(wb, exportFileName("xlsx"));
        } catch (e) {
            console.error(e);
            alert("Export failed. Please try again.");
        } finally { setExporting(null); }
    };

    const exportPDF = async () => {
        setExporting("pdf");
        try {
            // Loaded on demand — keeps ~200 kB of export libs out of the main bundle.
            const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
                import("jspdf"),
                import("jspdf-autotable"),
            ]);
            const rows = await fetchAllForExport();
            if (!rows.length) { alert("No transactions to export."); return; }

            const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFontSize(16);
            doc.text("Bet History Report", 40, 40);

            doc.setFontSize(9);
            doc.setTextColor(120);
            const filterText = userId ? `User ID: ${userId}` : searchTerm ? `Search: ${searchTerm}` : "All transactions";
            doc.text(`${filterText}  |  Rows: ${rows.length}  |  Generated: ${fmtDate(new Date().toISOString())}`, 40, 58);

            const totals = rows.reduce(
                (a, tx) => {
                    const t = (tx.transaction_type || "").toLowerCase();
                    if (t === "bet") a.wagered += Math.abs(tx.amount);
                    if (t === "win") a.payout += Math.abs(tx.amount);
                    return a;
                },
                { wagered: 0, payout: 0 }
            );
            doc.text(
                `Total Wagered: $${fmtNum(totals.wagered)}   Total Payout: $${fmtNum(totals.payout)}   Net: $${fmtNum(totals.payout - totals.wagered)}`,
                40, 72
            );

            autoTable(doc, {
                startY: 88,
                head: [["#", "User", "User ID", "Game", "Coin", "Type", "Amount", "Date"]],
                body: rows.map((tx, i) => [
                    i + 1,
                    tx.user_name,
                    tx.user_id,
                    tx.game_title || "—",
                    (tx.currency_code || "").toUpperCase(),
                    (tx.transaction_type || "").toUpperCase(),
                    `${signedAmount(tx) >= 0 ? "+" : "-"}$${fmtNum(Math.abs(tx.amount))}`,
                    fmtDate(tx.transaction_timestamp),
                ]),
                styles: { fontSize: 7.5, cellPadding: 3 },
                headStyles: { fillColor: [43, 110, 245], textColor: 255, fontStyle: "bold" },
                alternateRowStyles: { fillColor: [245, 247, 252] },
                columnStyles: {
                    0: { cellWidth: 28 },
                    6: { halign: "right" },
                },
                didDrawPage: (d) => {
                    const page = doc.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.setTextColor(150);
                    doc.text(`Page ${d.pageNumber} of ${page}`, pageWidth - 80, doc.internal.pageSize.getHeight() - 20);
                },
            });

            doc.save(exportFileName("pdf"));
        } catch (e) {
            console.error(e);
            alert("Export failed. Please try again.");
        } finally { setExporting(null); }
    };

    const statCards = analytics ? [
        { label: "Total Bets", value: Number(analytics.summary.total_bets).toLocaleString(), icon: <BarChart3 size={22} />, gradient: "from-[#0EA5E9] to-[#06B6D4]", glow: "shadow-[0_0_30px_rgba(14,165,233,0.3)]" },
        { label: "Total Wins", value: Number(analytics.summary.total_wins).toLocaleString(), icon: <Trophy size={22} />, gradient: "from-[#10B981] to-[#059669]", glow: "shadow-[0_0_30px_rgba(16,185,129,0.3)]" },
        { label: "Total Wagered", value: `$${fmtNum(analytics.summary.total_wagered)}`, icon: <DollarSign size={22} />, gradient: "from-[#F59E0B] to-[#EF4444]", glow: "shadow-[0_0_30px_rgba(245,158,11,0.3)]" },
        { label: "Unique Players", value: Number(analytics.summary.unique_players).toLocaleString(), icon: <Users size={22} />, gradient: "from-[#2B6EF5] to-[#8B5CF6]", glow: "shadow-[0_0_30px_rgba(100,110,205,0.3)]" },
    ] : [];

    const summaryCards = analytics ? [
        { label: "Total Wins", value: Number(analytics.summary.total_wins).toLocaleString(), color: "text-[#4A85F7]" },
        { label: "Total Losses", value: Number(analytics.summary.total_losses).toLocaleString(), color: "text-emerald-400" },
        { label: "Total Payout", value: `$${fmtNum(analytics.summary.total_payout)}`, color: "text-amber-400" },
        { label: "Net Profit", value: `$${fmtNum(analytics.summary.net_profit)}`, color: Number(analytics.summary.net_profit) >= 0 ? "text-emerald-400" : "text-[#E01B4F]" },
    ] : [];

    return (
        <div className="p-4 md:p-6 min-h-screen text-[#F9F9F9] space-y-6">

            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Bet History & Analytics</h1>
                    <p className="text-[#878AA2] text-sm mt-1">Transaction history, analytics &amp; user insights</p>
                </div>
                <button onClick={() => { fetchAnalytics(); fetchTransactions(); }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#0E1831] hover:bg-[#162140] border border-[#1E2D55] rounded-lg text-sm transition-all">
                    <RefreshCw size={14} className={loading || analyticsLoading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {/* Stat Cards */}
            {analytics && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((c, i) => (
                        <div key={i} className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${c.gradient} ${c.glow} p-5 transition-transform hover:scale-[1.02]`}>
                            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
                            <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10" />
                            <div className="flex justify-between items-start relative z-10">
                                <div>
                                    <p className="text-[#F9F9F9]/70 text-xs uppercase tracking-wider font-medium">{c.label}</p>
                                    <p className="text-3xl font-extrabold mt-2 tracking-tight">{c.value}</p>
                                </div>
                                <div className="bg-white/20 p-2.5 rounded-lg backdrop-blur-sm">{c.icon}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 bg-[#0E1831] rounded-lg p-1 border border-[#1E2D55] w-fit">
                {([
                    { key: "overview" as const, label: "Overview", icon: <BarChart3 size={14} /> },
                    { key: "transactions" as const, label: "Transactions", icon: <Activity size={14} /> },
                ]).map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.key
                            ? "bg-[#2B6EF5] text-[#F9F9F9] shadow-lg shadow-[#2B6EF5]/20"
                            : "text-[#878AA2] hover:text-[#F9F9F9] hover:bg-[#162140]"}`}>
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* ── OVERVIEW TAB ── */}
            {activeTab === "overview" && analytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Summary Stats */}
                    <div className="bg-[#0E1831] rounded-xl border border-[#1E2D55] p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Activity size={16} className="text-[#2B6EF5]" />
                            <h3 className="font-semibold">Summary</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {summaryCards.map((c, i) => (
                                <div key={i} className="bg-[#0C0D1D] rounded-lg border border-[#1E2D55] p-4">
                                    <p className="text-[#878AA2] text-xs uppercase tracking-wider">{c.label}</p>
                                    <p className={`text-2xl font-bold mt-1 ${c.color}`}>{c.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* P&L Summary */}
                    <div className="bg-[#0E1831] rounded-xl border border-[#1E2D55] p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <DollarSign size={16} className="text-[#2B6EF5]" />
                            <h3 className="font-semibold">P&L Summary</h3>
                        </div>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-[#878AA2] text-sm">Total Wagered</span>
                                <span className="text-lg font-bold text-[#4A85F7]">${fmtNum(analytics.summary.total_wagered)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-[#878AA2] text-sm">Total Payout</span>
                                <span className="text-lg font-bold text-emerald-400 flex items-center gap-1"><ArrowUpRight size={16} />${fmtNum(analytics.summary.total_payout)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-[#878AA2] text-sm">Total Loss Amount</span>
                                <span className="text-lg font-bold text-[#E01B4F] flex items-center gap-1"><ArrowDownRight size={16} />${fmtNum(analytics.summary.total_loss_amount)}</span>
                            </div>
                            <div className="border-t border-[#1E2D55] pt-3 flex justify-between items-center">
                                <span className="text-[#8384A5] text-sm font-semibold">Net Profit (House)</span>
                                <span className={`text-lg font-bold ${Number(analytics.summary.net_profit) >= 0 ? "text-emerald-400" : "text-[#E01B4F]"}`}>
                                    ${fmtNum(analytics.summary.net_profit)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Game Breakdown */}
                    <div className="bg-[#0E1831] rounded-xl border border-[#1E2D55] p-5 lg:col-span-2">
                        <div className="flex items-center gap-2 mb-4">
                            <Filter size={16} className="text-[#2B6EF5]" />
                            <h3 className="font-semibold">Top Games by Bets</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[#878AA2] text-xs uppercase border-b border-[#1E2D55]">
                                        <th className="pb-2 text-left">#</th>
                                        <th className="pb-2 text-left">Game</th>
                                        <th className="pb-2 text-right">Bets</th>
                                        <th className="pb-2 text-right">Players</th>
                                        <th className="pb-2 text-right">Wagered</th>
                                        <th className="pb-2 text-right">Payout</th>
                                        <th className="pb-2 text-right">Net Profit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.gameBreakdown.map((g, i) => (
                                        <tr key={`${g.game_name}-${i}`} className="border-b border-[#1E2D55]/50 hover:bg-[#0C0D1D] transition">
                                            <td className="py-2 text-[#878AA2] text-xs">{i + 1}</td>
                                            <td className="py-2 font-medium">{g.game_name || "—"}</td>
                                            <td className="py-2 text-right font-mono text-[#8384A5]">{Number(g.bet_count).toLocaleString()}</td>
                                            <td className="py-2 text-right font-mono text-[#8384A5]">{Number(g.players).toLocaleString()}</td>
                                            <td className="py-2 text-right font-mono text-[#4A85F7]">${fmtNum(g.wagered)}</td>
                                            <td className="py-2 text-right font-mono text-emerald-400">${fmtNum(g.payout)}</td>
                                            <td className={`py-2 text-right font-mono ${Number(g.net_profit) >= 0 ? "text-emerald-400" : "text-[#E01B4F]"}`}>${fmtNum(g.net_profit)}</td>
                                        </tr>
                                    ))}
                                    {analytics.gameBreakdown.length === 0 && (
                                        <tr><td colSpan={7} className="text-center py-8 text-[#878AA2]">No data yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ── TRANSACTIONS TAB ── */}
            {activeTab === "transactions" && (
                <div className="bg-[#0E1831] rounded-xl border border-[#1E2D55] overflow-hidden">
                    {/* Filters */}
                    <div className="p-4 border-b border-[#1E2D55] flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            Transactions
                            <span className="text-sm text-[#878AA2] font-normal ml-2">({pagination.totalCount || 0} total)</span>
                        </h2>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#878AA2]" />
                                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Search name, game..."
                                    className="bg-[#0C0D1D] border border-[#1E2D55] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2B6EF5] text-[#F9F9F9] placeholder-[#878AA2] w-52" />
                            </div>
                            <div className="relative">
                                <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#878AA2]" />
                                <input value={userId || ""} onChange={e => { setUserId(e.target.value || null); setPagination(p => ({ ...p, currentPage: 1 })); }}
                                    placeholder="User ID"
                                    className="bg-[#0C0D1D] border border-[#1E2D55] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2B6EF5] text-[#F9F9F9] placeholder-[#878AA2] w-40" />
                            </div>
                            {(searchTerm || userId) && (
                                <button onClick={handleClear}
                                    className="flex items-center gap-1 text-xs text-[#878AA2] hover:text-[#F9F9F9] bg-[#162140] px-3 py-2 rounded-lg transition">
                                    <X size={12} /> Clear
                                </button>
                            )}

                            <div className="flex gap-2">
                                <button onClick={exportExcel} disabled={exporting !== null}
                                    title="Download all filtered rows as Excel"
                                    className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 px-3 py-2 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed">
                                    <FileSpreadsheet size={13} /> {exporting === "excel" ? "Exporting..." : "Excel"}
                                </button>
                                <button onClick={exportPDF} disabled={exporting !== null}
                                    title="Download all filtered rows as PDF"
                                    className="flex items-center gap-1.5 text-xs font-medium text-[#E01B4F] hover:text-[#f04a72] bg-[#E01B4F]/10 hover:bg-[#E01B4F]/20 border border-[#E01B4F]/30 px-3 py-2 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed">
                                    <FileText size={13} /> {exporting === "pdf" ? "Exporting..." : "PDF"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[#878AA2] text-xs uppercase tracking-wider bg-[#0C0D1D]">
                                    <th className="px-4 py-3">User</th>
                                    <th className="px-4 py-3">Game</th>
                                    <th className="px-4 py-3">Coin</th>
                                    <th className="px-4 py-3">Type</th>
                                    <th className="px-4 py-3 text-right">Amount</th>
                                    <th className="px-4 py-3">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="text-center py-12 text-[#878AA2]">Loading...</td></tr>
                                ) : transactions.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-12 text-[#878AA2]">No transactions found</td></tr>
                                ) : (
                                    transactions.map((tx, idx) => (
                                        <tr key={tx.id} className={`border-t border-[#1E2D55] hover:bg-[#0E1831] transition ${idx % 2 === 0 ? "bg-[#0E1831]" : "bg-[#0E1831]"}`}>
                                            <td className="px-4 py-3">
                                                <span className="font-medium">{tx.user_name}</span>
                                                <span className="text-[#878AA2] text-xs block">#{tx.user_id}</span>
                                            </td>
                                            <td className="px-4 py-3 text-[#8384A5]">{tx.game_title || "—"}</td>
                                            <td className="px-4 py-3">
                                                <span className="bg-[#2B6EF5]/20 text-[#2B6EF5] text-xs font-semibold px-2 py-0.5 rounded">{tx.currency_code || "—"}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${TYPE_COLORS[tx.transaction_type] || "bg-[#1E2D55]/20 text-[#878AA2]"}`}>
                                                    {tx.transaction_type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right font-mono">
                                                <span className={(tx.transaction_type || "").toLowerCase() === "win" ? "text-emerald-400" : (tx.transaction_type || "").toLowerCase() === "loss" ? "text-[#E01B4F]" : "text-[#8384A5]"}>
                                                    {(tx.transaction_type || "").toLowerCase() === "win" ? "+" : (tx.transaction_type || "").toLowerCase() === "loss" ? "-" : ""}
                                                    ${fmtNum(Math.abs(tx.amount))}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-[#878AA2] text-xs">{fmtDate(tx.transaction_timestamp)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-[#1E2D55]">
                            <span className="text-xs text-[#878AA2]">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </span>
                            <div className="flex gap-2">
                                <button onClick={() => setPagination(p => ({ ...p, currentPage: Math.max(1, p.currentPage - 1) }))}
                                    disabled={pagination.currentPage <= 1}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-[#162140] hover:bg-[#1E2D55] rounded-lg text-xs transition disabled:opacity-40">
                                    <ChevronLeft size={14} /> Prev
                                </button>
                                <button onClick={() => setPagination(p => ({ ...p, currentPage: Math.min(p.totalPages, p.currentPage + 1) }))}
                                    disabled={pagination.currentPage >= pagination.totalPages}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-[#162140] hover:bg-[#1E2D55] rounded-lg text-xs transition disabled:opacity-40">
                                    Next <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default History;
